import clsx from 'clsx';
import { useState } from 'react';
import { BiChevronDown, BiSolidShip } from 'react-icons/bi';

import { getServiceId } from '@/api/tokens.helper';
import { imageVariantLink } from '@/common/media';
import { Avatar } from '@/components';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useNoYachtFallback } from '@/hooks/useNoYachtFallback';
import { useRoles } from '@/hooks/useRoles';
import { useAuth } from '@/store/auth/auth.store';
import { usePerson } from '@/store/persons/person.store';
import { useCurrentServiceProvider } from '@/store/service-providers/current-service-provider.store';
import { useAccountServiceProviderQuery } from '@/store/service-providers/service-providers.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { isGuestOnly, UserRole } from '@/utils/roles';

import { AccountSwitchContent } from './AccountSwitchContent';
import { AddAccountFlow } from './AddAccountFlow';
import s from './DesktopHeaderAccountSwitch.module.scss';

export const DesktopHeaderAccountSwitch = () => {
  const NoYachtFallback = useNoYachtFallback();
  const [opened, setOpened] = useState(false);
  const [addAccountOpened, setAddAccountOpened] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpened(false));

  const { userRoles } = useRoles();
  const services = useAuth((st) => st.session?.services);
  const { currentUser } = usePerson();
  const { vessel } = useVessels();
  const selectedServiceProviderId = useCurrentServiceProvider((s) => s.selectedServiceProviderId);

  const isServiceProvider = userRoles?.includes(UserRole.SERVICE_PROVIDER) ?? false;

  const { data: spData } = useAccountServiceProviderQuery(selectedServiceProviderId ?? '');

  const activeServiceId = getServiceId();
  const roleLabel =
    currentUser?.services?.find((service) => service.service_id === activeServiceId)?.role ??
    currentUser?.services?.[0]?.role;
  const role = roleLabel ? roleLabel.charAt(0).toUpperCase() + roleLabel.slice(1) : null;

  // A guest gets no Manage or Add account, and with a single vessel nothing to switch to; one who
  // has only just signed up is on no vessel yet and gets no switcher at all.
  const accountCount = new Set(services?.map(({ service_id }) => service_id)).size;
  const guestOnly = isGuestOnly(services);
  const locked = guestOnly && accountCount === 1;

  const isSp = isServiceProvider && !!selectedServiceProviderId;
  const name = isSp ? (spData?.name ?? '') : (vessel?.vessel_name ?? '');
  const image = isSp
    ? imageVariantLink(spData?.logo, 32)
    : (imageVariantLink(vessel?.image, 32) ?? NoYachtFallback);

  if (accountCount === 0) return null;

  return (
    <div className={s.root} ref={ref}>
      <button
        type="button"
        className={s.trigger}
        onClick={() => setOpened((o) => !o)}
        disabled={locked}
        aria-haspopup={locked ? undefined : 'listbox'}
        aria-expanded={locked ? undefined : opened}
        aria-label={`${role ?? ''} on ${name}${locked ? '' : '. Switch vessel'}`}
      >
        <span className={s.avatar} aria-hidden="true">
          {image ? <Avatar src={image} firstName={name || 'A'} size={32} /> : <BiSolidShip />}
        </span>
        <span className={s.identity}>
          <span className={s.name}>{name}</span>
          {role && <span className={s.role}>{role}</span>}
        </span>
        {!locked && (
          <BiChevronDown className={clsx(s.chevron, opened && s.chevronOpen)} aria-hidden="true" />
        )}
      </button>
      {opened && (
        <div className={s.menu}>
          <AccountSwitchContent
            canManage={!guestOnly}
            onClose={() => setOpened(false)}
            onAddAccount={() => {
              setOpened(false);
              setAddAccountOpened(true);
            }}
          />
        </div>
      )}
      <AddAccountFlow open={addAccountOpened} onClose={() => setAddAccountOpened(false)} />
    </div>
  );
};
